/**
 * This file contains all the non-admin api actions, required for case submmittion.
 * The callback should receive the following object:
 * {
 *   responseCode: number,
 *   responseBody: string/JSON
 * }
 *
 * There is an incosistency in the parameters order... By accident :\
 * Any way, the correct format should be func(school, callback, [extra specific params], timeCalled)
 */
import 'whatwg-fetch';
import Constants from "./Constants";

// We need to prevent a racing condition in which, for example, a case is sending a save
//  request, then the user returns to the main page, and only then the request
//  returns and the callback is called. In subch case, an error will occurr since
//  the callback's component is no longer mounted.
// To prevent it, each api call is being passed with the time when it was sent
//  (more accuratly, when the api was called). We save an array of all these
//  timestamps, and we expect all the requests who were sent at these times.
// If a request arrives and it's not in the array, we throw it away.
// A caller component may save all the times when he sent api requests in his
//  state. When he gets unmounted, he can ask API to remove all the requests
//  he still waits for from the waiting list
var waitingList = [];

function addToWaitingList(when) { waitingList.push(when); }
function removeFromWaitingList(whenArr) {
  if (!whenArr)
    whenArr = [];
  else if(typeof whenArr == "number")
    whenArr = [whenArr];

  for (const when of whenArr) {
    let index = waitingList.indexOf(when);
    if(index != -1)
      while(index != -1) {
        waitingList.splice(index, 1);
        index = waitingList.indexOf(when);
      }
  }
}

function handleCallback(callback, resp, whenCalled) {
    // first, set the cookie if one was sent
    if(resp.headers.get("set-cookie"))
      document.cookie = resp.headers.get("set-cookie");

    let ret = {  responseCode: resp.status }

    resp.text().then(
        (respText) => {
          // First try to parse to json
          try { ret.responseBody = JSON.parse(respText) }
          // If fails, return as text
          catch(e) {ret.responseBody = respText }

          const timeIndex = waitingList.indexOf(whenCalled);
          if(timeIndex != -1) {
            callback(ret);
            waitingList.splice(timeIndex, 1);
          }
        }
    )
}

function getJSON(uri, callback, whenCalled) {
    addToWaitingList(whenCalled);

    // Try to fetch members
    fetch(uri, {
        method: "GET",
        headers: {
            "Accept": "application/json"
        },
        credentials: 'include'
    }) // if success, call the callback
      .then((resp) => handleCallback(callback, resp, whenCalled))
      .catch(err => callback(undefined)); // else, call it with undefined
}

function postJSON(uri, data, callback, whenCalled) {
    addToWaitingList(whenCalled);

    fetch(uri, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(data)
    }) // if success, call the callback
      .then((resp) => handleCallback(callback, resp, whenCalled))
      .catch(err => callback(undefined)); // else, call it with undefined
}

function deleteJSON(uri, callback, whenCalled) {
    addToWaitingList(whenCalled);

    fetch(uri, {
        method: "DELETE",
        headers: {
            "Accept": "application/json"
        },
        credentials: 'include'
    })
      .then((resp) => handleCallback(callback, resp, whenCalled))
      .catch(err => callback(undefined));
}

// The callback will receive the schoolObj, or undefined if an error occurred
function getSchoolObj(schoolName, callback, whenCalled) {
    getJSON("/api/" + schoolName, callback, whenCalled);
}

function updateSchool(schoolName, callback, newData, whenCalled) {
    postJSON("/api/" + schoolName + "/update", newData, callback, whenCalled);
}

// This function sets all the members' value key to be their name,
//  since autocomplete input requires the objects to have a "value" field,
function renameMembersNameKeys(originalMembersArr){
    if(!originalMembersArr)
      return [];

    for (var i = 0; i < originalMembersArr.length; i++)
        if(originalMembersArr[i])
            originalMembersArr[i].value = originalMembersArr[i].name;

    return originalMembersArr;
}

// The callback will receive a list of all students, or undefined if an error occurred
function getAllMembers(schoolName, callback, whenCalled) {
    // before calling the callback, add "value" field to members
    // Since autocomplete input requires the object to have a "value" field,
    //  set the members' value field to be the name field
    getJSON("/api/" + schoolName + "/all_members", (resp) => {
        if(resp != undefined)
            renameMembersNameKeys(resp.responseBody);

        callback(resp);
    }, whenCalled);
}

function postCase(schoolName, data, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/submit_case", data, callback, whenCalled);
}

function postAppeal(schoolName, data, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/submit_appeal", data, callback, whenCalled);
}

function getCase(schoolName, callback, caseID, whenCalled) {
    caseID = caseID.toUpperCase();
    getJSON("/api/" + schoolName + "/case_by_id/" + caseID, callback, whenCalled);
}

function getAppeal(schoolName, callback, appealID, whenCalled) {
    appealID = appealID.toUpperCase();
    getJSON("/api/" + schoolName + "/appeal_by_id/" + appealID, callback, whenCalled);
}

function getPunishments(schoolName, callback, whenCalled) {
    getJSON("/api/" + schoolName + "/all_punishments", callback, whenCalled);
}

function addPunishment(schoolName, punishmentObject, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/add_punishment", punishmentObject, callback, whenCalled);
}

function deletePunishment(schoolName,  callback, punID, whenCalled) {
    deleteJSON("/api/" + schoolName + "/delete_punishment/" + punID, callback, whenCalled);
}

function updateCase(schoolName, caseID, caseData, callback, whenCalled) {
    caseID = caseID.toUpperCase();
    postJSON("/api/" + schoolName + "/update_case/" + caseID, caseData, callback, whenCalled);
}

function updateAppeal(schoolName, appealID, appealData, callback, whenCalled) {
    appealID = appealID.toUpperCase();
    postJSON("/api/" + schoolName + "/update_appeal/" + appealID, appealData, callback, whenCalled);
}

function updateCases(schoolName, casesArr, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/assign_cases", casesArr, callback, whenCalled);
}

function deleteCase(schoolName,  callback, caseID, whenCalled) {
    caseID = caseID.toUpperCase();
    deleteJSON("/api/" + schoolName + "/delete_case/" + caseID, callback, whenCalled);
}

function deleteAppeal(schoolName,  callback, appealID, whenCalled) {
    appealID = appealID.toUpperCase();
    deleteJSON("/api/" + schoolName + "/delete_appeal/" + appealID, callback, whenCalled);
}

function getUnhandledCases(schoolName, callback, whenCalled) {
    getJSON("/api/" + schoolName + "/all_cases?handled=false", callback, whenCalled);
}

function getUnhandledAppeals(schoolName, callback, whenCalled) {
    getJSON("/api/" + schoolName + "/all_appeals?handled=false", callback, whenCalled);
}

// This method receives memberID so that it will have the same parameters
//  as updateMember, for elegant switch between them in MemberInfo's submit function
function createMember(schoolName, memberID, memberData, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/add_member", memberData, callback, whenCalled);
}

function updateMember(schoolName, memberID, memberData, callback, whenCalled) {
    memberData.update_only = "true";
    memberData.id = memberID;
    createMember(schoolName, memberID, memberData, callback, whenCalled);
}

// Updates an array of members
function updateMembers(schoolName, membersArr, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/update_members", membersArr, callback, whenCalled);
}

function deleteMember(schoolName, memberID, callback, whenCalled) {
    deleteJSON("/api/" + schoolName + "/delete_member/" + memberID, callback, whenCalled);
}

function authenticate(schoolName, password, callback, whenCalled) {
  postJSON("/api/" + schoolName + "/login", { password }, callback, whenCalled);
}

function addVerdict(schoolName, verdictObject, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/add_verdict", verdictObject, callback, whenCalled);
}

function removeVerdict(schoolName, verdictID, callback, whenCalled) {
    deleteJSON("/api/" + schoolName + "/delete_verdict/" + verdictID, callback, whenCalled);
}

function cancelVerdict(schoolName, verdictID, callback, whenCalled) {
    postJSON("/api/" + schoolName + "/cancel_verdict/" + verdictID, {}, callback, whenCalled);
}

function getVerdictsByCase(schoolName, callback, caseID, whenCalled) {
    caseID = caseID.toUpperCase();
    getJSON("/api/" + schoolName + "/get_verdicts?caseId=" + caseID, callback, whenCalled);
}

function getVerdictsByMember(schoolName, memberID, callback, whenCalled) {
    getJSON("/api/" + schoolName + "/get_verdicts?memberId=" + memberID, callback, whenCalled);
}

function queryCases(schoolName, callback, query, whenCalled) {
    postJSON("/api/" + schoolName + "/all_cases", query, callback, whenCalled);
}

function getErrorMessage(resp, defaultMsg) {
    if (resp && resp.responseCode >= 400 && resp.responseCode < 500) {
        let msg = null;
        if (resp.responseBody && resp.responseBody.message) {
            msg = resp.responseBody.message;
        } else if (typeof resp.responseBody === 'string') {
            msg = resp.responseBody;
        }

        if (msg) {
            return Constants.API_ERROR_STR_ENG_HEB_MAPPING[msg] || msg;
        }
    }
    return defaultMsg;
}

export default {
    getErrorMessage,
    getSchoolObj,
    updateSchool,
    getAllMembers,
    renameMembersNameKeys,
    postCase,
    postAppeal,
    getCase,
    getAppeal,
    getUnhandledCases,
    getUnhandledAppeals,
    updateAppeal,
    updateCase,
    updateCases,
    deleteCase,
    deleteAppeal,
    createMember,
    updateMembers,
    updateMember,
    deleteMember,
    getPunishments,
    addPunishment,
    deletePunishment,
    authenticate,
    addVerdict,
    getVerdictsByCase,
    removeVerdict,
    cancelVerdict,
    getVerdictsByMember,
    addToWaitingList,
    removeFromWaitingList,
    queryCases,
};
